import React, { useEffect, useState } from "react";
import { CButton, CCard, CCardHeader, CCol, CRow, CSpinner, CTooltip, CBadge } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilPlus, cilFilter, cilReload, cilX } from "@coreui/icons";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate, useLocation } from "react-router-dom";
import DownloadExcel from "src/reusables/DownloadExcel";
import formatter from "src/reusables/formatter";
import { getAll } from "src/services/Stocks";
import { Toaster } from "src/components/Toaster";
import { usePaginationState } from "src/reusables/usePaginationState";

function Stocks() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [toaster, setToaster] = useState({ open: false, message: "", color: "success" });
  const navigate = useNavigate();
  const location = useLocation();
  const [paginationModel, setPaginationModel] = usePaginationState();

  const excelName = "Daftar Stok per " + formatter("dateOnly", new Date());

  const columns = [
    { field: "no", headerName: "No", width: 60, headerAlign: "center", align: "center" },
    { field: "visitationID", headerName: "Visitation ID", width: 140 },
    { field: "productName", headerName: "Product", flex: 1 },
    { field: "stock", headerName: "Stock", width: 120, headerAlign: "center", align: "center" },
    { field: "storeName", headerName: "Store", width: 200 },
    { field: "createdBy", headerName: "Created By", width: 150 },
  ];

  async function fetchData() {
    try {
      setLoading(true);
      const res = await getAll(filters);
      const data = res.stocks || [];
      const mapped = data.map((item, i) => ({
        no: i + 1,
        id: item.id,
        productID: item.product_id ?? item.productID,
        productName: item.product_name ?? item.productName,
        storeID: item.store_id ?? item.storeID,
        storeName: item.store_name ?? item.store_name,
        visitationID: item.visitation_id ?? item.visitationID,
        userID: item.user_id ?? item.userID,
        createdBy: item.created_by ?? "System",
        stock: item.stock,
      }));
      setRows(mapped);
    } catch (err) {
      setToaster({ open: true, message: "Gagal memuat data stok", color: "danger" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [filters]);

  useEffect(() => {
    if (location.state?.page !== undefined) {
      setPaginationModel({
        page: location.state.page,
        pageSize: location.state.pageSize || paginationModel.pageSize,
      });
    }
  }, [location.state]);

  const handleRowClick = (params) => {
    navigate(`/stocks/actions/${params.row.id}`, {
      state: { page: paginationModel.page, pageSize: paginationModel.pageSize },
    });
  };

  // filter helpers
  const getFilterLabel = (key) => ({ product: "Produk", category: "Kategori", store: "Toko", status: "Status" })[key] || key;
  const getFilterDisplay = (key, value) => value;
  const handleRemoveFilter = (key) => setFilters((prev) => ({ ...prev, [key]: "" }));
  const handleResetFilters = () => setFilters({});

  return (
    <div style={{ color: "black" }}>
      <Toaster
        open={toaster.open}
        message={toaster.message}
        setOpen={(val) => setToaster((p) => ({ ...p, open: val }))}
        color={toaster.color}
      />
      <CCard>
        <CCardHeader className="p-3 p-md-4">
          <div className="d-flex flex-column gap-3">
            <CRow className="align-items-center g-3">
              <CCol xs={12} md={6} className="pe-md-3">
                <h4 className="mb-0">Daftar Stok</h4>
              </CCol>
              <CCol xs={12} md={6} className="ps-md-3">
                <div className="d-flex flex-wrap justify-content-md-end gap-2">
                  <CTooltip content="Refresh Data">
                    <CButton color="light" onClick={fetchData} disabled={loading} className="p-2" shape="pill">
                      <CIcon icon={cilReload} />
                    </CButton>
                  </CTooltip>
                  <CTooltip content="Filter Data">
                    <CButton color="light" onClick={() => setFilterOpen(true)} className="p-2" shape="pill">
                      <CIcon icon={cilFilter} />
                    </CButton>
                  </CTooltip>
                  <CButton color="primary" onClick={() => navigate("/stocks/actions")} className="px-3">
                    <CIcon icon={cilPlus} className="me-2" />
                    Tambah Stok
                  </CButton>
                  <DownloadExcel rows={rows} columns={columns} excelName={excelName} className="ms-2" />
                </div>
              </CCol>
            </CRow>

            {Object.keys(filters).some((k) => filters[k]) && (
              <div className="d-flex flex-wrap align-items-center gap-2">
                <small className="text-muted me-2">Filter Aktif:</small>
                {Object.entries(filters).map(
                  ([key, value]) =>
                    value && (
                      <CBadge key={key} color="primary" className="d-flex align-items-center py-2 px-3" shape="rounded-pill">
                        <span className="me-2">{getFilterLabel(key)}: {getFilterDisplay(key, value)}</span>
                        <div role="button" onClick={(e) => { e.stopPropagation(); handleRemoveFilter(key); }} className="d-flex align-items-center">
                          <CIcon icon={cilX} width={14} />
                        </div>
                      </CBadge>
                    )
                )}
                <CButton color="link" size="sm" className="text-decoration-none p-0 ms-2" onClick={handleResetFilters}>
                  Clear All
                </CButton>
              </div>
            )}
          </div>
        </CCardHeader>

        <div style={{ margin: "16px", minHeight: "400px" }}>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
              <CSpinner color="primary" />
            </div>
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              onRowClick={handleRowClick}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 20, 25]}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                "& .MuiDataGrid-cell:focus": { outline: "none" },
                "& .MuiDataGrid-columnHeader:focus": { outline: "none" },
              }}
            />
          )}
        </div>
      </CCard>
      {/* TODO: Implement filter modal similar to stores/Filters if backend supports stock filters */}
    </div>
  );
}

export default Stocks;

